import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { DataTable } from '../../components/ui/DataTable'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'

type Broadcast = {
  id: number
  title: string
  audience: string
  scheduledAt: string | null
  sentAt: string | null
  status: string
  sentCount: number
  openRate: string
}

function formatDate(value: string | null) {
  if (!value) return 'Send now'
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export function BroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)

  useEffect(() => {
    setLoading(true)
    api.get(`/admin/notifications/broadcasts?page=${page}&limit=${limit}`)
      .then(({ data }) => {
        setBroadcasts(data.broadcasts ?? [])
        setTotal(data.total ?? 0)
      })
      .catch(() => setError('Unable to load broadcasts.'))
      .finally(() => setLoading(false))
  }, [page, limit])

  const rows = broadcasts.map((broadcast) => [
    broadcast.title,
    broadcast.audience,
    formatDate(broadcast.scheduledAt ?? broadcast.sentAt),
    broadcast.status.toUpperCase(),
    String(broadcast.sentCount ?? 0),
    broadcast.openRate ?? '0%',
  ])

  return (
    <div className="space-y-6">
      <PageTitle
        title="Scheduled Broadcasts"
        subtitle="Scheduled and sent broadcasts with status, delivery counts, open rate, cancel, and duplicate actions."
        action={<NavLink to="/notifications/broadcasts/new"><Button icon={Plus}>New Broadcast</Button></NavLink>}
      />
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      <DataTable
        title={loading ? 'Loading broadcasts...' : rows.length ? 'Broadcast List' : 'No broadcasts yet'}
        columns={['Title', 'Audience', 'Scheduled For', 'Status', 'Sent Count', 'Open Rate']}
        rows={rows}
        hideEditAction
        hideDeleteAction
        limit={limit}
        onLimitChange={(nextLimit) => { setLimit(nextLimit); setPage(1) }}
        onPageChange={setPage}
        page={page}
        totalRows={total}
      />
    </div>
  )
}
